import { useCallback, useEffect, useState } from "react";
import { ChevronDown, History, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";

const SOURCE_LABEL = {
  CALCULO_ORIGINAL: "cálculo original",
  ATUALIZACAO_MANUAL: "atualização manual",
};

/** Histórico de custos: versões registradas, nunca sobrescritas. */
export function CostHistoryPanel({ products }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    product_id: "",
    date_from: "",
    date_to: "",
    order: "desc",
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      );
      const { data } = await api.get("/pricing/cost-history", { params });
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));

  return (
    <Surface className="min-w-0 space-y-4 p-5" data-testid="cost-history-panel">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-brand" />
        <h2 className="font-display text-base md:text-lg">Histórico de custos</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Cada atualização de custo fica guardada aqui. Versões antigas nunca são apagadas nem
        recalculadas com os preços de hoje.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Buscar produto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.q}
              onChange={(e) => set("q")(e.target.value)}
              placeholder="Nome do produto"
              className="pl-9"
              data-testid="cost-history-search"
            />
          </div>
        </Field>
        <Field label="Produto">
          <NativeSelect
            value={filters.product_id}
            onChange={set("product_id")}
            placeholder="Todos os produtos"
            data-testid="cost-history-product"
            options={(products || []).map((p) => ({ value: p.id, label: p.name }))}
          />
        </Field>
        <Field label="De">
          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => set("date_from")(e.target.value)}
            data-testid="cost-history-from"
          />
        </Field>
        <Field label="Até">
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => set("date_to")(e.target.value)}
            data-testid="cost-history-to"
          />
        </Field>
        <Field label="Ordenar por data" className="sm:col-span-2">
          <NativeSelect
            value={filters.order}
            onChange={set("order")}
            data-testid="cost-history-order"
            options={[
              { value: "desc", label: "Mais recentes primeiro" },
              { value: "asc", label: "Mais antigos primeiro" },
            ]}
          />
        </Field>
      </div>

      {busy ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Carregando...</p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="cost-history-empty">
          Nenhuma versão de custo registrada ainda. Use o botão "Atualizar custo" no produto.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="cost-history-list">
          {rows.map((v) => {
            const expanded = open === v.id;
            return (
              <li
                key={v.id}
                className="rounded-xl border border-white/10 bg-secondary/30"
                data-testid={`cost-history-row-${v.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : v.id)}
                  className="flex w-full items-start justify-between gap-3 p-3 text-left"
                  data-testid={`cost-history-toggle-${v.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm">{v.product_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {dataBR(v.created_at)} · {SOURCE_LABEL[v.source] || v.source}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-num text-sm">{brl(v.new_cost)}</p>
                    {v.previous_cost != null && (
                      <p
                        className={`font-num text-[11px] ${
                          v.diff > 0 ? "text-warning" : v.diff < 0 ? "text-brand" : "text-muted-foreground"
                        }`}
                      >
                        antes {brl(v.previous_cost)} · {v.diff > 0 ? "+" : v.diff < 0 ? "− " : ""}
                        {brl(Math.abs(v.diff || 0))}
                        {v.diff_percent != null && ` (${v.diff_percent > 0 ? "+" : ""}${v.diff_percent}%)`}
                      </p>
                    )}
                    {v.previous_cost == null && (
                      <p className="text-[11px] text-muted-foreground">custo original registrado</p>
                    )}
                  </div>
                  <ChevronDown
                    className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                      expanded ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {expanded && (
                  <ul
                    className="space-y-1 border-t border-white/10 p-3 text-xs"
                    data-testid={`cost-history-detail-${v.id}`}
                  >
                    {(v.filaments || []).map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex justify-between gap-2">
                        <span className="truncate text-muted-foreground">
                          {f.name} · {f.grams} g · R$ {Number(f.cost_per_g).toFixed(4)}/g
                        </span>
                        <span className="font-num">{brl(f.cost)}</span>
                      </li>
                    ))}
                    {v.costs ? (
                      <>
                        <li className="flex justify-between gap-2 border-t border-white/10 pt-1">
                          <span className="text-muted-foreground">Materiais</span>
                          <span className="font-num">{brl(v.costs.material)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Energia</span>
                          <span className="font-num">{brl(v.costs.energy)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Máquina</span>
                          <span className="font-num">{brl(v.costs.machine)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Materiais do produto</span>
                          <span className="font-num">{brl(v.costs.supplies)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Materiais adicionais</span>
                          <span className="font-num">{brl(v.costs.extras)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Embalagem</span>
                          <span className="font-num">{brl(v.costs.packaging)}</span>
                        </li>
                        <li className="flex justify-between gap-2 border-t border-white/10 pt-1">
                          <span className="text-muted-foreground">Subtotal antes do refugo</span>
                          <span className="font-num">{brl(v.subtotal ?? v.costs.subtotal)}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                          <span className="text-muted-foreground">
                            Refugo ({v.waste_percent_applied ?? v.waste?.percent ?? 0}%
                            {v.waste_overridden ? ` · revisado, original ${v.inherited_waste_percent}%` : ""})
                          </span>
                          <span className="font-num">{brl(v.costs.waste)}</span>
                        </li>
                        <li className="flex justify-between gap-2 border-t border-white/10 pt-1">
                          <span className="text-muted-foreground">
                            Total do cálculo ({v.quantity} un)
                          </span>
                          <span className="font-num">{brl(v.costs.total)}</span>
                        </li>
                      </>
                    ) : (
                      <li className="text-amber-300">
                        Versão histórica sem composição registrada (não verificada).
                      </li>
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Surface>
  );
}

/** Botão de acesso ao histórico. */
export function CostHistoryToggle({ open, onToggle }) {
  return (
    <Button
      variant="outline"
      onClick={onToggle}
      className="gap-2 rounded-full"
      data-testid="cost-history-btn"
    >
      <History className="h-4 w-4" />
      {open ? "Fechar histórico" : "Histórico de custos"}
    </Button>
  );
}
